import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import {Add, Refresh, Delete} from '@material-ui/icons';
import {BackButton, hasAtLeastOnePermission, createHistory} from '../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization } from '../../sc-utilities';
import RoleDelete from '../../sc-roles/Roles/RoleDelete';
import Toolbar from '../../sc-utilities/Toolbar'
import * as action from "../../np-core/Auth/store/authActions";
import * as preselectionActions from "./store/deviceCasePreselectionActions";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    folderStructureIcon: {
        marginRight: theme.spacing(),
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const DeviceCasePreselectionList = props => {
    const { classes, translations, permissions, tableColumns, redirectHandler, tableRef, activeCustomerID, saveColumnsFilter, onFetchDeviceCasePreselections, onBulkDeleteDeviceCasePreselections, isAdminView } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, ids: [] });

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['system_device_case_preselection_delete', 'device_case_preselection_delete' ]);

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = false;
            if (rowData.CustomerID === null && !hasPermission(permissions,'system_device_case_preselection_delete')) {
                disabled = true;
            }
            rowData.tableData.disabled = disabled;
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {        
        tableRef.current.onQueryChange();
    }, [isAdminView])
    
    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        const q = filterQuery(query.filters?? []);
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        delete query["filters"];
        delete query["orderDirection"];
        delete query["search"];
        delete query["totalCount"];
        delete query['CustomerID'];

        if(isAdminView){query['CustomerID'] = null;}   
             
        const result = await onFetchDeviceCasePreselections(query);
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data || [], page: query.page, pageSize: pageSize, totalCount: result.count || 0 };
    }

    const redirectPath = isAdminView ? '/admin/device-case-preselection' : '/settings/device_case_preselection';

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['device_case_preselection'] || 'Device case preselection'}</Typography>
                {hasAtLeastOnePermission(permissions, ['system_device_case_preselection_create', 'device_case_preselection_create']) &&
                <Fab
                    id="create_device"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`${redirectPath}/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab>}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>

            </div>
        </div>
    };
    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, deviceCasePreselectionID: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteDeviceCasePreselections({ DeviceCasePreselectionIDs: deleteModalState.deviceCasePreselectionID, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, deviceCasePreselectionID: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = deviceCasePreselectionID => {
        setDeleteModalState({ ...deleteModalState, open: true, deviceCasePreselectionID: deviceCasePreselectionID });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["device_case_preselection"] || "Device case preselection"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(device => device.DeviceCasePreselectionID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`DeviceCasePreselection`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }
    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasAtLeastOnePermission(permissions, ['system_device_case_preselection_delete', 'device_case_preselection_delete']) &&
            <RoleDelete
                entityName={translations['device_case_preselection'] || 'Device case preselection'}
                translations={translations}
                handleCloseDanger={handleCloseDanger}
                handleCloseSafe={handleCloseSafe}
                deleteModalState={deleteModalState}
            />}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onFetchDeviceCasePreselections: data => dispatch(preselectionActions.fetchDeviceCasePreselections(data)),
        onBulkDeleteDeviceCasePreselections: data => dispatch(preselectionActions.bulkDeleteDeviceCasePreselections(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(DeviceCasePreselectionList));

